import { Flags } from "@/classes/FeatureFlags";
import { appDownloadUrl } from "@/utils/appDownloadUrl";
import { canAccessCheckForms } from "@/services/pageAccess";

// Visibility predicate receives a context object:
//   has(flag)   → boolean (feature flag is on)
//   role(name)  → boolean (current user has this role)
//   dev         → boolean (dev mode active)
//   isMobile    → boolean (mobile layout)
//   isMobileOS  → boolean (native iOS/Android)
//
// Each item targets exactly one of:
//   route   → router-link to a named route
//   path    → router-link to a path
//   href    → external anchor URL or a function that resolves it
//   action  → emits ('action', actionName) for parent to handle

export const sections = [
    {
        id: "dashboard",
        layout: "stack",
        items: [
            {
                id: "dashboard",
                label: "Dashboard",
                icon: "home",
                path: "/",
                visible: () => true
            }
        ]
    },
    {
        id: "production",
        title: "Production",
        items: [
            {
                id: "intakes",
                label: "Intakes",
                icon: "boxes",
                path: "/intakes",
                visible: ({ has }) =>
                    has(Flags.MeatModule) ||
                    has(Flags.IngredientsModule) ||
                    has(Flags.PackagingModule) ||
                    has(Flags.DeliveriesModule) ||
                    has(Flags.StockModule)
            },
            {
                id: "recipes",
                label: "Recipes",
                icon: "hat-chef",
                route: "RecipesView",
                visible: ({ has }) => has(Flags.RecipesModule)
            },
            {
                id: "products",
                label: "Products",
                icon: "burger-soda",
                route: "ProductsView",
                visible: ({ has }) => has(Flags.ProductsModule)
            },
            {
                id: "dispatches",
                label: "Dispatches",
                icon: "truck",
                route: "DispatchesView",
                visible: ({ has }) => has(Flags.DispatchModule)
            },
            {
                id: "production",
                label: "Production",
                icon: "industry",
                route: "ProductionView",
                visible: ({ has, role }) => has(Flags.MultiRecipeProduction) && !role("auditor")
            }
        ]
    },
    {
        id: "records",
        title: "Records",
        items: [
            {
                id: "temps",
                label: "Temps",
                icon: "temperature-high",
                route: "TemperatureView",
                visible: ({ has }) => has(Flags.TemperatureLogging)
            },
            {
                id: "checks",
                label: "Checks",
                icon: "clipboard-check",
                route: "ChecksView",
                visible: ({ has }) => has(Flags.ChecksModule)
            },
            {
                id: "foodtemps",
                label: "Food Temps",
                icon: "thermometer",
                route: "FoodRecordsView",
                visible: ({ has }) => has(Flags.FoodRecords)
            },
            {
                id: "clockin",
                label: "Clock In/Out",
                icon: "user-clock",
                route: "UserClockView",
                visible: ({ has, role }) => has(Flags.UserClockModule) && !role("auditor")
            },
            {
                id: "schedule",
                label: "My Schedule",
                icon: "calendar-alt",
                route: "MySchedule",
                visible: ({ has, role }) => has(Flags.RotaModule) && !role("auditor")
            },
            {
                id: "issues",
                label: "Issues",
                icon: "exclamation-triangle",
                route: "Issues",
                visible: ({ has }) => has(Flags.IssuesModule)
            },
            {
                id: "wastage",
                label: "Wastage",
                icon: "trash-alt",
                route: "WastageView",
                visible: ({ has }) => has(Flags.WastageModule)
            }
        ]
    },
    {
        id: "reports",
        title: "Reports",
        items: [
            {
                id: "monitoring",
                label: "Monitoring",
                icon: "monitor-heart-rate",
                route: "Monitor",
                visible: ({ has }) => has(Flags.SensorModule)
            },
            {
                id: "reports",
                label: "Reports",
                icon: "analytics",
                route: "Reports",
                visible: ({ role }) => role("admin") || role("auditor") || role("user")
            },
            {
                id: "dailydash",
                label: "Daily dash",
                icon: "chart-line",
                route: "DailyDashView",
                visible: ({ role }) => role("admin") || role("auditor")
            }
        ]
    },
    {
        id: "business",
        title: "Business",
        items: [
            {
                id: "allergens",
                label: "Allergens",
                icon: "exclamation-circle",
                route: "AllergensView",
                visible: ({ has }) => has(Flags.AllergensModule)
            },
            {
                id: "menus",
                label: "Menus",
                icon: "book-open",
                route: "MenusView",
                visible: ({ has }) => has(Flags.MenusModule)
            },
            {
                id: "checkforms",
                label: "Check forms",
                icon: "file-alt",
                route: "CheckFormsView",
                visible: canAccessCheckForms
            },
            {
                id: "haccp",
                label: "HACCP plans",
                icon: "clipboard-list",
                route: "HACCPPlans",
                visible: ({ has }) => has(Flags.HACCPModule)
            },
            {
                id: "labels",
                label: "Labels",
                icon: "tags",
                route: "Labels",
                visible: ({ has, role }) => has(Flags.LabelTemplates) && role("admin")
            },
            {
                id: "audits",
                label: "Audits",
                icon: "clipboard-list",
                route: "Audits",
                visible: ({ has }) => has(Flags.AuditModule)
            },
            {
                id: "complaints",
                label: "Complaints",
                icon: "comment-exclamation",
                route: "Complaints",
                supplierAllowed: true,
                visible: ({ has, role }) => has(Flags.ComplaintsModule) || role("supplier")
            },
            {
                id: "files",
                label: "Files",
                icon: "folders",
                route: "FilesView",
                visible: ({ has }) => has(Flags.FilesModule)
            },
            {
                id: "training",
                label: "Training",
                icon: "graduation-cap",
                route: "Training",
                visible: ({ has, role }) => has(Flags.TrainingModule) && role("admin")
            },
            {
                id: "rota",
                label: "Staff rota",
                icon: "calendar-week",
                route: "Rota",
                visible: ({ has, role }) => has(Flags.RotaModule) && role("admin")
            },
            {
                id: "chat",
                label: "Team chat",
                icon: "comments",
                route: "Chat",
                visible: ({ has, role }) =>
                    has(Flags.TeamChatModule) && (role("admin") || role("user"))
            },
            {
                id: "announcements",
                label: "Announcements",
                icon: "bullhorn",
                route: "Announcements",
                visible: ({ role }) => role("admin")
            }
        ]
    },
    {
        id: "settings",
        layout: "stack",
        boxed: true,
        visible: ({ role }) => role("admin") || role("auditor"),
        items: [
            {
                id: "settings",
                label: "Settings",
                icon: "cog",
                path: "/settings",
                visible: () => true
            }
        ]
    },
    {
        id: "support",
        layout: "stack",
        boxed: true,
        visible: () => true,
        items: [
            {
                id: "support",
                label: "Support",
                icon: "comment-smile",
                action: "support",
                visible: () => true
            }
        ]
    },
    {
        id: "promo",
        layout: "stack",
        boxed: true,
        visible: ({ isMobile, isMobileOS }) => isMobile && !isMobileOS,
        items: [
            {
                id: "promo",
                label: "Try our Android or iOS app",
                icon: "mobile-alt",
                href: () => appDownloadUrl(),
                dataCy: "app-download",
                visible: () => true
            }
        ]
    },
    {
        id: "profile",
        layout: "footer",
        items: [
            {
                id: "profile",
                label: "Profile",
                icon: "user-circle",
                path: "/profile",
                supplierAllowed: true,
                visible: ({ role }) => role("admin") || role("user") || role("supplier")
            },
            {
                id: "signout",
                label: "Sign Out",
                icon: "sign-out-alt",
                action: "logout",
                dataCy: "logout-button",
                supplierAllowed: true,
                visible: () => true
            }
        ]
    }
];

// Resolve visibility for the whole tree.
// Returns sections array with only visible items, sections with zero visible items omitted.
//
// Suppliers are external parties (their API scope is just complaints + their own
// profile per backend/api/policy.csv). To avoid exposing menu items they can't
// access, items must explicitly opt in via `supplierAllowed: true` to render
// for a supplier. The role()-exclusion predicates used elsewhere (!role('user')
// etc.) incidentally include suppliers, so this allowlist is the safety net.
export function visibleSections(ctx) {
    const isSupplier = ctx.role("supplier");
    return sections
        .map((section) => {
            if (section.visible && !section.visible(ctx)) return null;
            let items = section.items.filter((item) => item.visible(ctx));
            if (isSupplier) {
                items = items.filter((item) => item.supplierAllowed === true);
            }
            if (items.length === 0) return null;
            items = items.map((item) =>
                typeof item.href === "function" ? { ...item, href: item.href() } : item
            );
            return { ...section, items };
        })
        .filter(Boolean);
}
